import {MenuItem, Select, SelectChangeEvent, Card, CardContent, Tooltip, LinearProgress, Box, Chip} from "@mui/material";
import React, {useContext, useEffect, useState, useMemo, useRef} from "react";
import {AdminProjectContext} from "../../../contexts/AdminProjectContext";
import {
    CheckCircle,
    Warning,
    Analytics,
    MonetizationOn,
    TrendingDown,
    AccountBalance,
    People,
    TrendingUp
} from "@mui/icons-material";
import "../../../styles/AdminActiveProjectMonthlyDataView.css";
import useAxios from "../../../hooks/useAxios";
import {ADMIN_PROJECT_MONTHLY_DATA_URL} from "../../../utils/constant";
import {ProjectMonthlyData, ProjectMonthlyDataFromBackend} from "../../../interface/AdminDashboard.interface";
import { logger } from '../../../utils/logger';



export function AdminActiveProjectMonthlyDataView() {
    const {selectedProject} = useContext(AdminProjectContext);
    const {getWithAxios} = useAxios();

    // Ref pour éviter les doubles appels (React StrictMode)
    const lastFetchedProjectId = useRef<string | null>(null);

    // State
    const [selectedProjectMonth, setSelectedProjectMonth] = useState<string>("");
    const [projectMonthlyData, setProjectMonthlyData] = useState<ProjectMonthlyData | undefined>();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);


    const handleOnProjectMonthSelection = (e: SelectChangeEvent<unknown>) => {
        const newMonth = e.target.value as string;  // Format: "YYYY-MM"
        logger.debug("Selected month:", newMonth);
        setSelectedProjectMonth(newMonth);
    };

    const getIconComponent = (iconType: string) => {
        switch (iconType) {
            case 'MonetizationOn':
                return <MonetizationOn/>;
            case 'TrendingDown':
                return <TrendingDown/>;
            case 'TrendingUp':
                return <TrendingUp/>;
            case 'Warning':
                return <Warning/>;
            case 'People':
                return <People/>;
            case 'AccountBalance':
                return <AccountBalance/>;
            default:
                return <Analytics/>;
        }
    };

    // Transformer les metrics du backend en details pour l'UI
    const transformBackendDataToFrontend = (backendData: ProjectMonthlyDataFromBackend): ProjectMonthlyData => {
        const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                           'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

        return {
            months: backendData.months.map(month => {
                // Générer displayName à partir de month (format "2025-01")
                const [year, monthNum] = month.month.split('-');
                const monthIndex = parseInt(monthNum) - 1;
                const displayName = `${monthNames[monthIndex]} ${year}`;

                // Calculer le solde net
                const netBalance = month.metrics.contributions - month.metrics.withdrawals - (month.metrics.penalties || 0);
                const currency = selectedProject?.currency || 'EUR';

                const formatValue = (val: number) => val.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

                return {
                    month: month.month,
                    year: month.year,
                    displayName,
                    details: [
                        {
                            id: 1,
                            value: month.metrics.contributions,
                            label: "Contributions",
                            formattedValue: `${formatValue(month.metrics.contributions)} ${currency}`,
                            variant: month.metrics.contributions > 0 ? "success" : "default",
                            iconType: "MonetizationOn",
                            tooltip: "Total des encaissements du mois"
                        },
                        {
                            id: 2,
                            value: month.metrics.withdrawals,
                            label: "Retraits",
                            formattedValue: `${formatValue(month.metrics.withdrawals)} ${currency}`,
                            variant: month.metrics.withdrawals > 0 ? "warning" : "default",
                            iconType: "TrendingDown",
                            tooltip: "Total des retraits effectués"
                        },
                        {
                            id: 3,
                            value: netBalance,
                            label: "Solde Net",
                            formattedValue: `${formatValue(netBalance)} ${currency}`,
                            variant: netBalance > 0 ? "success" : netBalance < 0 ? "error" : "default",
                            iconType: "AccountBalance",
                            tooltip: "Contributions - Retraits - Pénalités"
                        },
                        {
                            id: 4,
                            value: month.metrics.activeParticipants,
                            label: "Participants actifs",
                            formattedValue: `${month.metrics.activeParticipants}/${selectedProject?.Users?.length || 0}`,
                            variant: month.metrics.activeParticipants > 0 ? "success" : "default",
                            iconType: "People",
                            tooltip: "Nombre de participants ayant contribué ce mois"
                        },
                        // Ajouter les pénalités seulement si > 0
                        ...(month.metrics.penalties && month.metrics.penalties > 0 ? [{
                            id: 5,
                            value: month.metrics.penalties,
                            label: "Pénalités",
                            formattedValue: `${formatValue(month.metrics.penalties)} ${currency}`,
                            variant: "error" as const,
                            iconType: "Warning",
                            tooltip: "Total des pénalités appliquées"
                        }] : [])
                    ]
                };
            })
        };
    };

    // Calculs mémorisés pour le mois sélectionné
    const currentMonthData = useMemo(() => {
        return projectMonthlyData?.months.find(month => month.month === selectedProjectMonth);
    }, [projectMonthlyData, selectedProjectMonth]);

    // Résumé du mois
    const monthSummary = useMemo(() => {
        if (!currentMonthData) return null;

        const contributions = currentMonthData.details.find(d => d.label === "Contributions")?.value || 0;
        const withdrawals = currentMonthData.details.find(d => d.label === "Retraits")?.value || 0;
        const netBalance = currentMonthData.details.find(d => d.label === "Solde Net")?.value || 0;
        const activeParticipants = currentMonthData.details.find(d => d.label === "Participants actifs")?.value || 0;
        const totalParticipants = selectedProject?.Users?.length || 1;
        const participationRate = (activeParticipants / totalParticipants) * 100;

        let status = 'info';
        let message = '';

        if (contributions === 0) {
            status = 'warning';
            message = 'Aucune contribution ce mois';
        } else if (netBalance < 0) {
            status = 'error';
            message = 'Solde négatif - attention aux retraits';
        } else if (participationRate < 50) {
            status = 'warning';
            message = `Faible taux de participation (${participationRate.toFixed(0)}%)`;
        } else if (participationRate >= 80) {
            status = 'success';
            message = `Excellente participation (${participationRate.toFixed(0)}%)`;
        } else {
            status = 'info';
            message = `Participation: ${participationRate.toFixed(0)}%`;
        }

        return { status, message, participationRate };
    }, [currentMonthData, selectedProject]);

    // Extraire l'UUID pour une dépendance stable
    const selectedProjectUuid = selectedProject?.uuid;

    // Load monthly data when project UUID changes (pas l'objet entier)
    useEffect(() => {
        const loadMonthlyData = async () => {
            if (!selectedProjectUuid) {
                setProjectMonthlyData(undefined);
                setSelectedProjectMonth("");
                lastFetchedProjectId.current = null;
                return;
            }

            // Protection contre les doubles appels en StrictMode
            if (lastFetchedProjectId.current === selectedProjectUuid) {
                return;
            }
            lastFetchedProjectId.current = selectedProjectUuid;

            setIsLoading(true);
            setError(null);

            try {
                // Nouvelle API : retourne TOUS les mois en une seule requête
                const response = await getWithAxios(ADMIN_PROJECT_MONTHLY_DATA_URL(selectedProjectUuid, ""));
                logger.debug('Backend response:', response);

                // Le backend retourne { data: { months: [...] } } avec metrics
                const backendData: ProjectMonthlyDataFromBackend = {
                    months: response.data?.months || []
                };

                // Transformer les metrics en details pour l'UI
                const monthlyData = transformBackendDataToFrontend(backendData);

                setProjectMonthlyData(monthlyData);

                // Sélectionner automatiquement le mois le plus récent
                if (monthlyData.months.length > 0) {
                    setSelectedProjectMonth(monthlyData.months[monthlyData.months.length - 1].month);
                }

                setIsLoading(false);
            } catch (err) {
                logger.error('Error loading monthly data:', err);
                setError(err instanceof Error ? err.message : 'Erreur lors du chargement des données mensuelles');
                setProjectMonthlyData(undefined);
                setIsLoading(false);
            }
        };

        loadMonthlyData();
    }, [selectedProjectUuid]);


    if (isLoading) {
        return (
            <div className="monthly-data-container">
                <div className="section-header">
                    <div className="section-icon">
                        <Analytics/>
                    </div>
                    <h3 className="section-title">Chargement des données mensuelles...</h3>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="monthly-data-container">
                <div className="section-header">
                    <div className="section-icon">
                        <Analytics/>
                    </div>
                    <h3 className="section-title">Erreur: {error}</h3>
                </div>
            </div>
        );
    }

    return (
        <div className="monthly-data-container">
            {/* Header compact */}
            <div className="section-header">
                <div className="section-icon">
                    <Analytics/>
                </div>
                <h2 className="section-title">Données mensuelles</h2>
            </div>

            {/* Sélecteur de mois */}
            {selectedProject && projectMonthlyData && projectMonthlyData?.months?.length > 0 && (
                <div className="month-selector">
                    <Select
                        className="styled-select"
                        value={selectedProjectMonth?.toString()}
                        onChange={handleOnProjectMonthSelection}
                        fullWidth
                        displayEmpty
                        size="small"
                    >
                        {projectMonthlyData.months.map((month) => (
                            <MenuItem key={month.month} value={month.month}>
                                {month.displayName}
                            </MenuItem>
                        ))}
                    </Select>
                </div>
            )}

            {/* Résumé compact */}
            {monthSummary && (
                <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Chip
                            label={monthSummary.message}
                            // color={monthSummary.status as any}
                            size="small"
                            sx={{ fontSize: '0.7rem', height: 24 }}
                        />
                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{monthSummary.participationRate.toFixed(0)}%</span>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={monthSummary.participationRate}
                        color={monthSummary.participationRate >= 80 ? 'success' : monthSummary.participationRate >= 50 ? 'primary' : 'warning'}
                        sx={{ height: 4, borderRadius: 2 }}
                    />
                </Box>
            )}

            {/* Métriques compactes */}
            {selectedProjectMonth && projectMonthlyData && (
                <div className="metrics-grid">
                    {currentMonthData?.details.map(metric => (
                        <Tooltip key={metric.id} title={metric.tooltip || ''} placement="top">
                            <Card className={`metric-card ${metric.variant}`}>
                                <CardContent className="metric-card-content">
                                    <div className={`metric-icon-container ${metric.variant}`}>
                                        {getIconComponent(metric.iconType)}
                                    </div>
                                    <div className="metric-info">
                                        <p className="metric-label">{metric.label}</p>
                                        <h2 className="metric-value">{metric.formattedValue || metric.value}</h2>
                                    </div>
                                </CardContent>
                            </Card>
                        </Tooltip>
                    ))}
                </div>
            )}
        </div>
    );
}